import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  ArrowLeft, Upload, Download, CheckCircle2, XCircle, AlertTriangle, Loader, History, Search
} from "lucide-react";

const HEADERS = [
  "tool_no",
  "tool_life",
  "tool_type",
  "cutting_material",
  "pm_stroke_range_min",
  "pm_stroke_range_max",
];

const BulkToolSetup = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [retireMissing, setRetireMissing] = useState(false);
  const [retirePreview, setRetirePreview] = useState(null); // { would_retire, kept }
  const [syncResult, setSyncResult] = useState(null); // { retired }

  const loadHistory = () => {
    api.get(`/admin/bulk-import-log?days=90${search ? `&search=${encodeURIComponent(search)}` : ""}`)
      .then((r) => setHistory(r.data || []))
      .catch(() => {});
  };

  useEffect(() => { loadHistory(); }, []);

  const parseCsv = (text) => {
    const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
    if (!lines.length) return [];
    const headerLine = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idxs = HEADERS.map((h) => headerLine.indexOf(h));
    if (idxs[0] === -1) throw new Error("CSV must have a 'tool_no' column");
    return lines.slice(1).map((line) => {
      const cols = line.split(",").map((c) => c.trim());
      const row = {};
      HEADERS.forEach((h, i) => {
        const idx = idxs[i];
        if (idx === -1) return;
        const val = cols[idx];
        if (val == null || val === "") return;
        if (["tool_life", "pm_stroke_range_min", "pm_stroke_range_max"].includes(h)) {
          const n = parseInt(val);
          if (!isNaN(n)) row[h] = n;
        } else {
          row[h] = val;
        }
      });
      return row;
    }).filter((r) => r.tool_no);
  };

  const handleFile = (e) => {
    setError("");
    setResult(null);
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsv(reader.result);
        setRows(parsed);
        if (parsed.length === 0) setError("No valid rows parsed from CSV");
      } catch (err) {
        setError(err.message || "Failed to parse CSV");
      }
    };
    reader.readAsText(f);
  };

  const submit = async () => {
    if (rows.length === 0) return;
    setUploading(true);
    setError("");
    setSyncResult(null);
    try {
      const res = await api.post("/admin/tools/bulk-update", { rows });
      setResult(res.data);
      // If "retire missing" was checked, follow up with a bulk-sync call
      if (retireMissing) {
        const keep = rows.map((r) => r.tool_no).filter(Boolean);
        try {
          const sync = await api.post("/admin/tools/bulk-sync-active", { keep_codes: keep });
          setSyncResult(sync.data);
        } catch (se) {
          setError(se.response?.data?.detail || "Failed to retire missing tools");
        }
      }
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update");
    } finally {
      setUploading(false);
    }
  };

  const previewRetire = async () => {
    if (rows.length === 0) return;
    setError("");
    try {
      const keep = rows.map((r) => r.tool_no).filter(Boolean);
      const res = await api.post("/admin/tools/bulk-sync-active", { keep_codes: keep, dry_run: true });
      setRetirePreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to preview retire list");
    }
  };

  const downloadSample = () => {
    const sample = [
      HEADERS.join(","),
      "31,2000000,Press Tool,D2,76500,90000",
      "32,1500000,Draw Die,HSS,60000,75000",
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tool-bulk-setup-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto p-6">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2">
              <Upload size={22} className="text-[#1A3560]" /> Bulk Tool Setup
            </h1>
            <p className="text-sm text-slate-500">Upload a CSV to set tool_life, tool_type, cutting_material, and PM stroke range for many tools at once.</p>
          </div>
          <button
            onClick={downloadSample}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 px-3 py-2 rounded-lg"
          >
            <Download size={14} /> Sample CSV
          </button>
        </div>

        {/* CSV format hint */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4 text-xs text-blue-900">
          <p className="font-extrabold mb-1">CSV columns (header row required, order doesn&apos;t matter):</p>
          <code className="font-mono text-blue-700 block">
            {HEADERS.join(",")}
          </code>
          <p className="mt-2">
            <b>tool_no</b> is required. All other columns are optional — leave blank to keep existing value.
          </p>
        </div>

        {/* Upload */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-4">
          <label className="block cursor-pointer">
            <input
              data-testid="bulk-csv-input"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="hidden"
            />
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-[#1A3560] transition-colors">
              <Upload size={32} className="mx-auto text-slate-400 mb-2" />
              <p className="font-bold text-slate-700">Click to select a CSV file</p>
              <p className="text-xs text-slate-500 mt-1">or drop it here</p>
            </div>
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 mb-4 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {/* Preview */}
        {rows.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-4">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                Preview — {rows.length} row(s) parsed
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <label
                  data-testid="retire-missing-toggle"
                  className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg cursor-pointer select-none"
                  title="Any active tool whose tool_no is NOT in this upload will be marked inactive (soft-retired). History is preserved."
                >
                  <input
                    data-testid="retire-missing-checkbox"
                    type="checkbox"
                    checked={retireMissing}
                    onChange={(e) => {
                      setRetireMissing(e.target.checked);
                      setRetirePreview(null);
                    }}
                    className="h-3.5 w-3.5 accent-amber-600"
                  />
                  Retire tools not in this upload
                </label>
                {retireMissing && (
                  <button
                    data-testid="preview-retire-btn"
                    onClick={previewRetire}
                    className="text-xs font-bold text-amber-800 bg-white border border-amber-300 hover:bg-amber-50 px-3 py-1.5 rounded-lg"
                  >
                    Preview retire list
                  </button>
                )}
                <button
                  data-testid="bulk-submit"
                  onClick={submit}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-sm font-extrabold text-white bg-[#1A3560] hover:bg-[#152a4d] disabled:bg-slate-300 px-4 py-2 rounded-lg"
                >
                  {uploading ? <Loader className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                  {uploading ? "Applying…" : `Apply to ${rows.length} tool(s)`}
                </button>
              </div>
            </div>
            {retireMissing && retirePreview && (
              <div
                data-testid="retire-preview-banner"
                className="mx-4 my-3 border border-amber-200 bg-amber-50 rounded-xl px-4 py-3 text-xs text-amber-900"
              >
                <p className="font-extrabold mb-1 inline-flex items-center gap-1.5">
                  <AlertTriangle size={14} />
                  {retirePreview.would_retire?.length || 0} tool(s) will be retired on Apply
                </p>
                {retirePreview.would_retire?.length > 0 ? (
                  <p className="font-mono text-amber-800 break-all">
                    {retirePreview.would_retire.join(", ")}
                  </p>
                ) : (
                  <p className="italic">No active tools are missing from the upload — nothing will be retired.</p>
                )}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    {HEADERS.map((h) => (
                      <th key={h} className="px-3 py-2 text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="bulk-preview-tbody">
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      {HEADERS.map((h) => (
                        <td key={h} className="px-3 py-1.5 font-mono">{r[h] ?? "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Result</p>
            </div>
            <div className="grid grid-cols-3 gap-3 p-4">
              <ResultCard label="Updated" value={result.updated} color="text-emerald-700" bg="bg-emerald-50" icon={<CheckCircle2 size={16} />} />
              <ResultCard label="Skipped" value={result.skipped} color="text-slate-500" bg="bg-slate-50" icon={<AlertTriangle size={16} />} />
              <ResultCard label="Not Found" value={result.not_found} color="text-red-700" bg="bg-red-50" icon={<XCircle size={16} />} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Tool No</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Fields</th>
                  </tr>
                </thead>
                <tbody data-testid="bulk-result-tbody">
                  {result.results.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-3 py-1.5 font-bold">{r.tool_no}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${
                          r.status === "updated" ? "bg-emerald-100 text-emerald-700" :
                          r.status === "not_found" ? "bg-red-100 text-red-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-slate-500">{(r.fields || []).join(", ") || r.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Retired tools result */}
        {syncResult && (
          <div
            data-testid="sync-result-card"
            className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden mt-4"
          >
            <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 flex items-center justify-between">
              <p className="text-xs font-extrabold uppercase tracking-widest text-amber-800 inline-flex items-center gap-1.5">
                <AlertTriangle size={12} /> Retired tools not in upload
              </p>
              <p className="text-xs font-extrabold text-amber-800">{syncResult.retired?.length || 0} retired</p>
            </div>
            <div className="p-4 text-xs">
              {syncResult.retired?.length > 0 ? (
                <p className="font-mono text-slate-700 break-all">{syncResult.retired.join(", ")}</p>
              ) : (
                <p className="italic text-slate-500">No tools were retired.</p>
              )}
            </div>
          </div>
        )}

        {/* Import history */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
            <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
              <History size={12} /> Import history (last 90 days)
            </p>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2 top-2 text-slate-400" />
                <input
                  data-testid="history-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadHistory(); }}
                  placeholder="actor or tool_no..."
                  className="pl-7 pr-2 py-1.5 text-xs border border-slate-300 rounded-lg outline-none"
                />
              </div>
              <button
                onClick={loadHistory}
                className="text-xs font-bold text-white bg-[#1A3560] hover:bg-[#152a4d] px-3 py-1.5 rounded-lg"
              >
                Search
              </button>
            </div>
          </div>
          {history.length === 0 ? (
            <div className="text-center text-slate-400 py-6 text-sm">No imports yet</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="history-list">
              {history.map((h) => (
                <li key={h.import_id} className="px-4 py-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs">
                      <b className="text-slate-900">{h.actor_name || h.actor_id}</b>
                      <span className="text-slate-400"> · {new Date(h.timestamp).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-emerald-700 font-extrabold">{h.updated} updated</span>
                      <span className="text-slate-500">{h.skipped} skipped</span>
                      <span className="text-red-600 font-extrabold">{h.not_found} not found</span>
                      <span className="text-slate-400">of {h.total_rows}</span>
                    </div>
                  </div>
                  <details className="mt-1">
                    <summary className="text-[10px] font-bold text-slate-500 cursor-pointer">Show diff ({h.results.length} row(s))</summary>
                    <div className="overflow-x-auto mt-2">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                          <tr>
                            <th className="px-2 py-1 text-left">Tool</th>
                            <th className="px-2 py-1 text-left">Status</th>
                            <th className="px-2 py-1 text-left">Changes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {h.results.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-2 py-1 font-bold">{r.tool_no}</td>
                              <td className="px-2 py-1">
                                <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full ${
                                  r.status === "updated" ? "bg-emerald-100 text-emerald-700" :
                                  r.status === "not_found" ? "bg-red-100 text-red-700" :
                                  "bg-slate-100 text-slate-600"
                                }`}>{r.status}</span>
                              </td>
                              <td className="px-2 py-1 text-slate-600">
                                {r.diff
                                  ? Object.entries(r.diff).map(([k, v]) => (
                                      <span key={k} className="mr-2">
                                        <b>{k}:</b> <span className="text-slate-400">{String(v.from ?? "\u2014")}</span> → <span className="text-emerald-700 font-bold">{String(v.to)}</span>
                                      </span>
                                    ))
                                  : (r.reason || (r.fields || []).join(", ") || "\u2014")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const ResultCard = ({ label, value, color, bg, icon }) => (
  <div className={`${bg} rounded-xl p-3`}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
      <div className={color}>{icon}</div>
    </div>
    <p className={`text-2xl font-extrabold ${color}`}>{value}</p>
  </div>
);

export default BulkToolSetup;
